import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Folder, FileText, Image as ImageIcon, File as FileIcon, Download, ChevronRight, HardDrive, Loader2, Lock } from "lucide-react";
import { API } from "@/lib/api";

const prettySize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const kindIcon = (ct = "") => (ct.startsWith("image/") ? ImageIcon : ct === "application/pdf" ? FileText : FileIcon);
const pwQuery = (pw) => (pw ? `?pw=${encodeURIComponent(pw)}` : "");

export default function SharedDrive() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [needPw, setNeedPw] = useState(false);
  const [pw, setPw] = useState("");
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState("");

  const fetchMeta = useCallback((pwVal) => {
    setLoading(true);
    return axios.get(`${API}/drive/shared/${token}`, { params: { pw: pwVal || undefined } })
      .then((r) => {
        if (r.data.protected && !r.data.name) { setNeedPw(true); setLoading(false); return; }
        setNeedPw(false); setPw(pwVal || ""); setMeta(r.data); setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        setError(e?.response?.status === 410 ? "Link wygasł." : "Link wygasł lub jest nieprawidłowy.");
      });
  }, [token]);

  useEffect(() => { fetchMeta(""); }, [fetchMeta]);

  const loadFolder = useCallback((pid) => {
    axios.get(`${API}/drive/shared/${token}/browse`, { params: { parent_id: pid || undefined, pw: pw || undefined } })
      .then((r) => setFolder(r.data))
      .catch((e) => setError(e?.response?.status === 410 ? "Link wygasł." : "Nie udało się wczytać folderu."));
  }, [token, pw]);

  useEffect(() => { if (meta?.type === "folder") loadFolder(meta.root_id); }, [meta, loadFolder]);

  const submitPw = (e) => {
    e.preventDefault();
    setPwError("");
    axios.get(`${API}/drive/shared/${token}`, { params: { pw: pwInput } })
      .then((r) => {
        if (r.data.protected && !r.data.name) { setPwError("Nieprawidłowe hasło."); return; }
        setNeedPw(false); setPw(pwInput); setMeta(r.data);
      })
      .catch(() => setPwError("Nieprawidłowe hasło."));
  };

  return (
    <div className="min-h-screen bg-[#0D1117] text-foreground">
      <header className="border-b border-border px-5 py-4">
        <p className="font-display text-xl font-extrabold uppercase tracking-tight">EMPRO <span className="text-amber-400">WORKWEAR</span></p>
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Dysk EMPRO · udostępnione</p>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8" data-testid="shared-drive">
        {loading ? (
          <div className="flex items-center gap-3 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-amber-400" />Ładowanie…</div>
        ) : error ? (
          <div className="rounded-xl border border-rose-400/40 bg-rose-400/10 p-6 text-center" data-testid="shared-error">
            <p className="font-semibold text-rose-300">{error}</p>
          </div>
        ) : needPw ? (
          <form onSubmit={submitPw} className="max-w-sm mx-auto rounded-xl border border-border bg-card p-6 text-center space-y-4" data-testid="shared-password-form">
            <Lock className="h-8 w-8 mx-auto text-amber-400" />
            <p className="font-semibold">Ten link jest chroniony hasłem</p>
            <input data-testid="shared-password-input" type="password" autoFocus value={pwInput} onChange={(e) => setPwInput(e.target.value)}
              placeholder="Wpisz hasło" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            {pwError && <p className="text-xs text-rose-400" data-testid="shared-password-error">{pwError}</p>}
            <button data-testid="shared-password-submit" type="submit" className="w-full rounded-lg bg-amber-400 px-4 py-2.5 font-bold uppercase tracking-wider text-[#0D1117] hover:bg-amber-300">Otwórz</button>
          </form>
        ) : meta?.type === "file" ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center" data-testid="shared-file">
            <div className="mx-auto mb-4 grid h-20 w-20 place-items-center rounded-xl bg-[#0D1117]">
              {(() => { const Icon = kindIcon(meta.content_type); return <Icon className="h-10 w-10 text-amber-400" />; })()}
            </div>
            <p className="text-lg font-semibold break-all">{meta.name}</p>
            <p className="text-sm text-muted-foreground mb-5">{prettySize(meta.size)}</p>
            <a data-testid="shared-file-download" href={`${API}/drive/shared/${token}/download${pwQuery(pw)}`}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-5 py-3 font-bold uppercase tracking-wider text-[#0D1117] hover:bg-amber-300">
              <Download className="h-4 w-4" />Pobierz plik
            </a>
          </div>
        ) : folder ? (
          <div className="space-y-5" data-testid="shared-folder">
            <div className="flex items-center gap-2 text-sm flex-wrap" data-testid="shared-breadcrumb">
              <HardDrive className="h-4 w-4 text-amber-400" />
              {folder.breadcrumb?.map((c, i) => (
                <span key={c.id} className="flex items-center gap-2">
                  {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                  <button data-testid={`shared-crumb-${c.id}`} onClick={() => loadFolder(c.id)} className="text-amber-400 hover:underline">{c.name}</button>
                </span>
              ))}
            </div>
            {(!folder.folders.length && !folder.files.length) ? (
              <p className="text-sm text-muted-foreground py-10 text-center">Ten folder jest pusty.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {folder.folders.map((fo) => (
                  <button key={fo.id} data-testid={`shared-folder-${fo.id}`} onClick={() => loadFolder(fo.id)}
                    className="rounded-xl border border-border bg-card p-4 text-left hover:border-amber-400/60 transition-colors">
                    <Folder className="h-10 w-10 text-amber-400 mb-2" />
                    <p className="text-sm font-semibold truncate">{fo.name}</p>
                  </button>
                ))}
                {folder.files.map((f) => {
                  const Icon = kindIcon(f.content_type);
                  const isImg = (f.content_type || "").startsWith("image/");
                  return (
                    <div key={f.id} data-testid={`shared-file-${f.id}`} className="group relative rounded-xl border border-border bg-card overflow-hidden hover:border-amber-400/60 transition-colors">
                      <div className="h-24 bg-[#0D1117] grid place-items-center">
                        {isImg && f.file_id ? <img src={`${API}/files/${f.file_id}`} alt="" className="h-full w-full object-contain" /> : <Icon className="h-9 w-9 text-amber-400" />}
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-semibold truncate">{f.name}</p>
                        <p className="text-[11px] text-muted-foreground">{prettySize(f.size)}</p>
                      </div>
                      <a data-testid={`shared-download-${f.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${f.download}${pwQuery(pw)}`} title="Pobierz"
                        className="absolute top-2 right-2 rounded-md bg-background/80 p-1.5 text-muted-foreground hover:text-amber-400"><Download className="h-4 w-4" /></a>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-amber-400" />Ładowanie folderu…</div>
        )}
      </main>
    </div>
  );
}
